import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type {
  AdminEnvelope,
  AdminLastSeen,
  AdminUserListRow,
  AdminUserSearchResponse,
  AdminUserSessionsResponse,
  AdminUsersListParams,
  AdminUsersListResponse,
} from '@/client/types/admin/users-orgs';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import useGetMutationFn from '../use-get-mutation-fn';
import useGetQueryFn from '../use-get-query-fn';

export const adminUserKeys = {
  all: ['admin', 'users'] as const,
  list: (params: AdminUsersListParams) =>
    [...adminUserKeys.all, 'list', params] as const,
  detail: (uid: number | null) => [...adminUserKeys.all, 'detail', uid] as const,
  sessions: (authid: string | null) =>
    [...adminUserKeys.all, 'sessions', authid] as const,
  lastSeen: (authid: string | null) =>
    [...adminUserKeys.all, 'last-seen', authid] as const,
  mfa: (uid: number) => [...adminUserKeys.all, 'mfa', uid] as const,
};

/** Accept either the bare paginated dict or the `{status, data}` envelope. */
const unwrapUsersList = (raw: any): AdminUsersListResponse => {
  const body = raw?.data && (raw.data.users !== undefined || raw.data.total_users !== undefined)
    ? raw.data
    : raw;
  return body ?? { page: 1, per_page: 0, total_users: 0, total_pages: 0, users: [] };
};

export const useAdminUsersQuery = (params: AdminUsersListParams = {}) => {
  const queryFn = useGetQueryFn<unknown>({
    defaultErrorMessage: 'An unexpected error occurred while fetching users',
    endpoint: '/api/admin/users',
    paginated: true,
  });

  return useQuery({
    queryKey: adminUserKeys.list(params),
    queryFn: async () => unwrapUsersList(await queryFn({ ...params })),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};


/** Accept either the `{status, data}` envelope or a bare `{user, roles}` row. */
const unwrapUserRow = (raw: any): AdminUserListRow | null => {
  const body = raw?.data?.user !== undefined ? raw.data : raw;
  return body?.user ? (body as AdminUserListRow) : null;
};

export const useAdminUserDetailQuery = (
  uid: number | null,
  options: {
    enabled?: boolean;
    placeholderRow?: AdminUserListRow | null;
  } = {}
) => {
  const { enabled = true, placeholderRow = null } = options;
  const hasUid = uid !== null && Number.isFinite(uid);

  const queryFn = useGetQueryFn<unknown>({
    defaultErrorMessage: 'An unexpected error occurred while fetching the user',
    endpoint: `/api/admin/users/${uid}`,
  });

  const query = useQuery({
    queryKey: adminUserKeys.detail(uid),
    queryFn: async () => unwrapUserRow(await queryFn({})),
    enabled: hasUid && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: placeholderRow ?? undefined,
    retry: (failureCount, error) =>
      (error as any)?.status !== 404 && failureCount < 1,
  });

  const notFound =
    query.isError && (query.error as any)?.status === 404;

  return {
    ...query,
    data: (query.data ?? null) as AdminUserListRow | null,
    notFound,
    isFetchError: query.isError && !notFound,
  };
};

/** `GET /api/admin/users/sessions/<authid>` — real, works as specced. */
export const useAdminUserSessionsQuery = (
  authid: string | null,
  enabled = true
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminUserSessionsResponse>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching sessions',
    endpoint: `/api/admin/users/sessions/${authid}`,
  });

  return useQuery({
    queryKey: adminUserKeys.sessions(authid),
    queryFn: () => queryFn({}),
    enabled: Boolean(authid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/** Last seen — `POST /api/admin/users/last_seen`. */
export const useAdminUserLastSeenQuery = (
  authid: string | null,
  enabled = true
) => {
  // POST, not GET — hence the mutation factory used inside a query. The proxy
  // returns the raw `{ status, data, … }` envelope, so type it as such and
  // unwrap `data` in `select` (mirrors the sessions query above).
  const fetchLastSeen = useGetMutationFn<
    { authid: string },
    AdminEnvelope<AdminLastSeen>
  >({
    defaultErrorMessage: 'An unexpected error occurred while fetching last seen',
    endpoint: '/api/admin/users/last_seen',
    method: 'POST',
  });

  return useQuery({
    queryKey: adminUserKeys.lastSeen(authid),
    queryFn: () => fetchLastSeen({ authid: authid as string }),
    enabled: Boolean(authid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useAdminUserSearchQuery = (
  query: string | null,
  enabled = true
) => {
  const search = query?.trim() ?? '';

  const queryFn = useGetQueryFn<AdminUserSearchResponse>({
    defaultErrorMessage: 'An unexpected error occurred while fetching users',
    endpoint: '/api/admin/users/search',
  });

  return useQuery({
    queryKey: adminUserKeys.list({ search }),
    queryFn: () => queryFn({ search }),
    enabled: Boolean(search) && enabled,
    // Keep the last results on screen while the next keystroke's search runs.
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response.data.users ?? [],
  });
};
export interface AdminUserMfa {
  uid: number;
  enabled: boolean;
  method: 'sms' | 'email' | null;
}

/** `GET /api/admin/users/<uid>/mfa` — two-step verification status. */
export const useAdminUserMfaQuery = (uid: number) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminUserMfa>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching two-step verification',
    endpoint: `/api/admin/users/${uid}/mfa`,
  });

  return useQuery({
    queryKey: adminUserKeys.mfa(uid),
    queryFn: () => queryFn({}),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};
